import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useLang } from '@/i18n/LangContext';
import Reveal from '@/components/Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AREA_ORDER = ['cardiology','neurology','antibiotics','gastro','pediatrics','womens_health','oncology_support','respiratory'];

export default function Products() {
  const { t, areaLabel, lang } = useLang();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [areas, setAreas] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const activeArea = params.get('area') || 'all';

  useEffect(() => {
    axios.get(`${API}/therapeutic-areas`).then((r) => setAreas(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/products`, { params: { therapeutic_area: activeArea, q: query || undefined } })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [activeArea, query]);

  const setArea = (id) => {
    const next = new URLSearchParams(params);
    if (id === 'all') next.delete('area'); else next.set('area', id);
    setParams(next, { replace: true });
  };

  const areaCount = useMemo(() => Object.fromEntries((areas || []).map((a) => [a.id, a.count])), [areas]);

  return (
    <div data-testid="products-page">
      <section className="mm-section" style={{ paddingTop: 72 }}>
        <div className="container-mm">
          <div className="eyebrow">{t.nav.products}</div>
          <h1 style={{ fontSize: 'clamp(48px, 6vw, 96px)', marginTop: 16 }}>{t.products.title}</h1>
          <p style={{ fontSize: 18, color: 'var(--mm-slate)', marginTop: 24, maxWidth: 720 }}>
            {t.products.lead}
          </p>

          <div className="mm-rx-strip" data-testid="rx-strip">
            <span className="rx-mark">Rx</span>
            <span>
              {lang === 'ru' && 'Информация о рецептурных препаратах предназначена для медицинских специалистов. Не является рекламой для пациентов.'}
              {lang === 'tj' && 'Иттилоот дар бораи доруҳои рецептӣ барои мутахассисони тиббӣ пешбинӣ шудааст.'}
              {lang === 'en' && 'Prescription drug information is intended for healthcare professionals only. Not for patient promotion.'}
            </span>
          </div>

          <div style={{ marginTop: 8, marginBottom: 20 }}>
            <input
              className="mm-search"
              placeholder={t.products.search_placeholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="products-search"
            />
          </div>

          <div className="mm-filters" data-testid="area-filters">
            <button
              className={`mm-chip ${activeArea === 'all' ? 'active' : ''}`}
              onClick={() => setArea('all')}
              data-testid="chip-all"
            >
              {t.products.all}
            </button>
            {AREA_ORDER.map((id) => (
              <button
                key={id}
                className={`mm-chip ${activeArea === id ? 'active' : ''}`}
                onClick={() => setArea(id)}
                data-testid={`chip-${id}`}
              >
                {areaLabel(id)} <span style={{ opacity: 0.6, marginLeft: 6 }}>{areaCount[id] || 0}</span>
              </button>
            ))}
          </div>

          {loading ? (
            <div style={{ padding: 40 }} className="eyebrow">Loading…</div>
          ) : products.length === 0 ? (
            <div style={{ padding: 40 }} className="eyebrow" data-testid="products-empty">{t.products.empty}</div>
          ) : (
            <div className="mm-products" data-testid="products-grid">
              {products.map((p, i) => (
                <Reveal key={p.id} delay={Math.min(i, 8) * 40}>
                  <div className="mm-product-card" data-testid={`product-${p.id}`}>
                    <div className="meta">
                      <span>{p.id}</span>
                      <span>{areaLabel(p.therapeutic_area)}</span>
                    </div>
                    <div className="name">{p.name}</div>
                    <div className="inn">{t.products.inn}: {p.inn}</div>
                    <div className="desc">{p.description}</div>
                    <div className="badges">
                      <span className="mm-badge">{t.products.form}: {p.form}</span>
                      <span className="mm-badge">{t.products.dosage}: {p.dosage}</span>
                      <span className={`mm-badge ${p.prescription ? 'rx' : 'otc'}`}>
                        {p.prescription ? t.products.rx : t.products.otc}
                      </span>
                    </div>
                    <div className="meta" style={{ marginTop: 8, borderTop: '1px solid var(--mm-line)', paddingTop: 10 }}>
                      <span>{t.products.manufacturer}</span>
                      <span style={{ color: 'var(--mm-ink)' }}>{p.manufacturer}</span>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
